import React, { useState, useCallback, useEffect } from 'react';
import { useApi } from '../../../../hooks/useApi';
import OzonDescriptionHistory from './OzonDescriptionHistory';
import OzonDelimModal from './OzonDelimModal';
import { getSeoTransfers, clearSeoTransfers } from '../../../../components/OzonSeoUniqualization';
import './OzonDescriptionPanel.css';
import { matchesSearch } from '../../../../utils/search';

const PAGE_SIZE = 50;

/**
 * Convert description HTML text (with <br>, <br/>, <br />) into OZON rich content JSON string.
 */
function descriptionToRich(description) {
    if (!description || !description.trim()) return '';

    const parts = description.split(/<br\s*\/?>/gi);
    const items = [];

    parts.forEach((part, i) => {
        const text = part.trim();
        if (text) {
            items.push({ type: 'text', content: text });
        }
        if (i < parts.length - 1) {
            items.push({ type: 'br' });
        }
    });

    while (items.length > 0 && items[items.length - 1].type === 'br') {
        items.pop();
    }

    if (items.length === 0) return '';

    const rich = {
        content: [
            {
                widgetName: 'raTextBlock',
                title: { items: [], size: 'size5', color: 'color1' },
                theme: 'primary',
                padding: 'type2',
                gapSize: 'm',
                text: { size: 'size2', align: 'left', color: 'color1', items: items },
            },
        ],
        version: 0.3,
    };

    return JSON.stringify(rich, null, 2);
}

function OzonDescriptionPanel({ accountId }) {
    const { request, showStatus } = useApi();

    // Products data
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Drafts: { [offer_id]: { name?, description?, rich_content? } }
    const [drafts, setDrafts] = useState({});

    // SEO transfer tracking: { [offer_id]: { originalDesc, seoText } }
    const [seoTransfers, setSeoTransfers] = useState({});

    // Multi-select
    const [selectedOfferIds, setSelectedOfferIds] = useState(new Set());

    // Search & pagination
    const [search, setSearch] = useState('');
    const [onlyChanged, setOnlyChanged] = useState(false);
    const [page, setPage] = useState(1);

    // View mode
    const [view, setView] = useState('editor'); // 'editor' | 'history'

    // Confirm modal
    const [showConfirm, setShowConfirm] = useState(false);
    const [submitResult, setSubmitResult] = useState(null);

    // Delimiter/reformat modal
    const [showDelimModal, setShowDelimModal] = useState(false);
    const [delimSnapshot, setDelimSnapshot] = useState(null); // { [offer_id]: prevDesc }

    // ------------------------------------------------------------------ //
    //  Load products                                                      //
    // ------------------------------------------------------------------ //
    const loadProducts = useCallback(async (resetAll = true) => {
        if (!accountId) return;
        setLoading(true);
        try {
            const data = await request(`/api/v1/ozon-desc/${accountId}/products`);
            setProducts(data.products || []);
            // Only reset drafts/seoTransfers on manual reload (button click)
            if (resetAll) {
                setDrafts({});
                setSeoTransfers({});
            }
            setSelectedOfferIds(new Set());
            setPage(1);
            if (resetAll) showStatus('success', `Загружено ${data.total} товаров`);
        } catch (err) {
            showStatus('error', `Ошибка загрузки: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [accountId, request, showStatus]);

    useEffect(() => {
        if (accountId) {
            loadProducts(false); // Don't reset transfers on initial load
        }
    }, [accountId, loadProducts]);

    // ------------------------------------------------------------------ //
    //  Apply SEO transfers queued on the server by other SEO tools       //
    // ------------------------------------------------------------------ //
    const applyPendingTransfers = useCallback(async () => {
        if (!accountId || products.length === 0) return;

        const pendingTransfers = await getSeoTransfers(request, accountId);
        if (pendingTransfers === null) {
            showStatus('error', 'Не удалось загрузить перенос из других SEO-инструментов');
            return;
        }
        if (Object.keys(pendingTransfers).length === 0) return;

        let appliedCount = 0;
        const newSeoTransfers = {};
        const newDrafts = {};

        for (const [offerId, transfer] of Object.entries(pendingTransfers)) {
            const product = products.find(p => p.offer_id === offerId);
            if (!product) continue;

            newSeoTransfers[offerId] = {
                originalDesc: product.description || '',
                seoText: transfer.seoText,
                name: transfer.name || ''
            };

            newDrafts[offerId] = {};
            if (transfer.seoText) {
                newDrafts[offerId].description = transfer.seoText;
            }
            if (transfer.name) {
                newDrafts[offerId].name = transfer.name;
            }

            appliedCount++;
        }

        if (appliedCount > 0) {
            setSeoTransfers(prev => ({ ...prev, ...newSeoTransfers }));
            setDrafts(prev => ({ ...prev, ...newDrafts }));
            // Из очереди убираем только применённые: товары, которых нет в списке, ждут дальше.
            await clearSeoTransfers(request, accountId, Object.keys(newSeoTransfers));
            showStatus('success', `Применено ${appliedCount} SEO-переносов из других инструментов`);
        }
    }, [accountId, products, request, showStatus]);

    // Check for transfers after products load
    useEffect(() => {
        applyPendingTransfers();
    }, [applyPendingTransfers]);

    // Очередь переноса лежит на сервере: опрашиваем редко и только пока вкладка открыта,
    // а при возврате на вкладку проверяем сразу.
    useEffect(() => {
        const check = () => { if (!document.hidden) applyPendingTransfers(); };
        const interval = setInterval(check, 15000);
        document.addEventListener('visibilitychange', check);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', check);
        };
    }, [applyPendingTransfers]);

    // ------------------------------------------------------------------ //
    //  Draft management                                                   //
    // ------------------------------------------------------------------ //
    const updateDraft = useCallback((offerId, field, value) => {
        setDrafts((prev) => {
            const existing = prev[offerId] || {};
            const original = products.find((p) => p.offer_id === offerId);
            if (!original) return prev;

            const origValue = original[field] || '';
            const isOriginal = value === origValue;

            const next = { ...existing, [field]: value };

            if (isOriginal) {
                delete next[field];
            }

            if (Object.keys(next).length === 0) {
                const { [offerId]: _, ...rest } = prev;
                return rest;
            }

            return { ...prev, [offerId]: next };
        });
    }, [products]);

    const changedCount = Object.keys(drafts).length;

    useEffect(() => {
        if (changedCount === 0 && onlyChanged) setOnlyChanged(false);
    }, [changedCount, onlyChanged]);

    // ------------------------------------------------------------------ //
    //  Multi-select                                                       //
    // ------------------------------------------------------------------ //
    const toggleSelect = useCallback((offerId) => {
        setSelectedOfferIds((prev) => {
            const next = new Set(prev);
            if (next.has(offerId)) {
                next.delete(offerId);
            } else {
                next.add(offerId);
            }
            return next;
        });
    }, []);

    // Без useCallback: функция фильтра пересоздаётся на каждую отрисовку, а с пустыми
    // зависимостями «Выбрать все» навсегда держал список с первой отрисовки (0 товаров).
    const selectAll = () => {
        setSelectedOfferIds(new Set(getFilteredProducts().map(p => p.offer_id)));
    };

    const selectNone = useCallback(() => {
        setSelectedOfferIds(new Set());
    }, []);

    // ------------------------------------------------------------------ //
    //  SEO Transfer Revert/Restore (arrows in table)                       //
    // ------------------------------------------------------------------ //
    // Revert SEO transfer (← arrow) - keeps seoTransfers entry so → can restore
    const revertSeoTransfer = useCallback((offerId) => {
        const transfer = seoTransfers[offerId];
        if (!transfer) return;

        // Check if user manually edited the text
        const currentDraft = drafts[offerId]?.description;
        const isManuallyEdited = currentDraft && currentDraft !== transfer.seoText && currentDraft !== transfer.originalDesc;

        if (isManuallyEdited) {
            const confirmed = window.confirm('Вы внесли ручные изменения. Вернуть оригинальное описание? Ваши изменения будут потеряны.');
            if (!confirmed) return;
        }

        updateDraft(offerId, 'description', transfer.originalDesc);
        // Don't remove from seoTransfers - keep it so user can restore with →
    }, [seoTransfers, drafts, updateDraft]);

    // Restore SEO transfer (→ arrow)
    const restoreSeoTransfer = useCallback((offerId) => {
        const transfer = seoTransfers[offerId];
        if (!transfer || typeof transfer.seoText !== 'string') return;

        updateDraft(offerId, 'description', transfer.seoText);
    }, [seoTransfers, updateDraft]);

    // Check if current description is SEO text
    const isSeoText = useCallback((offerId) => {
        const transfer = seoTransfers[offerId];
        if (!transfer) return false;

        const product = products.find(p => p.offer_id === offerId);
        const currentDesc = drafts[offerId]?.description ?? product?.description ?? '';
        return currentDesc === transfer.seoText;
    }, [seoTransfers, drafts, products]);

    // ------------------------------------------------------------------ //
    //  Rich from description                                              //
    // ------------------------------------------------------------------ //
    const richFromDescription = useCallback((offerId) => {
        const product = products.find((p) => p.offer_id === offerId);
        if (!product) return;

        const desc = (drafts[offerId]?.description) ?? (product.description || '');
        if (!desc.trim()) {
            showStatus('info', `Нет аннотации для ${offerId}`);
            return;
        }

        const richJson = descriptionToRich(desc);
        if (richJson) {
            updateDraft(offerId, 'rich_content', richJson);
        }
    }, [products, drafts, updateDraft, showStatus]);

    const handleDelimApply = useCallback((results) => {
        // results = { [offer_id]: newDesc }
        const snapshot = {};
        let count = 0;
        for (const [offerId, newDesc] of Object.entries(results)) {
            const product = products.find(p => p.offer_id === offerId);
            if (!product) continue;
            const current = (drafts[offerId]?.description) ?? (product.description || '');
            snapshot[offerId] = current;
            updateDraft(offerId, 'description', newDesc);
            count++;
        }
        if (count > 0) setDelimSnapshot(snapshot);
        setShowDelimModal(false);
        const scope = selectedOfferIds.size > 0 ? `(выделено: ${selectedOfferIds.size})` : '(все товары)';
        showStatus('info', `Переформатировано ${count} аннотаций ${scope}`);
    }, [products, drafts, selectedOfferIds, updateDraft, showStatus]);

    const undoDelimiterChange = useCallback(() => {
        if (!delimSnapshot) return;
        for (const [offerId, prevDesc] of Object.entries(delimSnapshot)) {
            updateDraft(offerId, 'description', prevDesc);
        }
        setDelimSnapshot(null);
        showStatus('info', 'Изменение разделителя откачено');
    }, [delimSnapshot, updateDraft, showStatus]);

    const massRichFromDescription = useCallback(() => {
        let count = 0;
        for (const product of products) {
            const currentRich = (drafts[product.offer_id]?.rich_content) ?? (product.rich_content || '');
            if (currentRich.trim()) continue;

            const desc = (drafts[product.offer_id]?.description) ?? (product.description || '');
            if (!desc.trim()) continue;

            const richJson = descriptionToRich(desc);
            if (richJson) {
                updateDraft(product.offer_id, 'rich_content', richJson);
                count++;
            }
        }
        showStatus('info', `Перенесено в Rich: ${count} товаров`);
    }, [products, drafts, updateDraft, showStatus]);

    // ------------------------------------------------------------------ //
    //  Filter & paginate                                                  //
    // ------------------------------------------------------------------ //
    const getFilteredProducts = useCallback(() => {
        const q = search.toLowerCase();
        return products.filter(
            (p) =>
                (!onlyChanged || Boolean(drafts[p.offer_id])) &&
                matchesSearch(p, q)
        );
    }, [products, search, onlyChanged, drafts]);

    // ------------------------------------------------------------------ //
    //  Paste handler                                                      //
    // ------------------------------------------------------------------ //
    const MULTIROW_PASTE_CONFIRM_THRESHOLD = 5;

    const handlePaste = useCallback((e, startIndex, field) => {
        const text = e.clipboardData?.getData('text/plain');
        if (!text) return;

        // If the clipboard text carries <br> markup, it's HTML-style multi-line
        // content meant for a SINGLE cell (e.g. an annotation with embedded line
        // breaks), not a column of values to spread across rows. Let the browser
        // perform its normal single-cell paste in that case.
        if (/<br\s*\/?>/i.test(text)) return;

        const lines = text.split(/\r?\n/).filter((l) => l.length > 0);
        if (lines.length <= 1) return;

        const filteredList = getFilteredProducts();
        const affectedRows = Math.min(lines.length, filteredList.length - startIndex);

        if (affectedRows > MULTIROW_PASTE_CONFIRM_THRESHOLD) {
            const confirmed = window.confirm(
                `Вставка затронет ${affectedRows} ячеек (строк), продолжить?`
            );
            if (!confirmed) {
                e.preventDefault();
                return;
            }
        }

        e.preventDefault();

        lines.forEach((line, i) => {
            const rowIdx = startIndex + i;
            if (rowIdx >= filteredList.length) return;
            const product = filteredList[rowIdx];
            updateDraft(product.offer_id, field, line.trim());
        });

        showStatus('info', `Вставлено ${affectedRows} строк`);
    }, [getFilteredProducts, updateDraft, showStatus]);

    const filtered = getFilteredProducts();
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageData = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    // ------------------------------------------------------------------ //
    //  Submit                                                             //
    // ------------------------------------------------------------------ //
    const handleSubmit = useCallback(async () => {
        const tooLong = Object.entries(drafts)
            .filter(([, fields]) => typeof fields.description === 'string' && fields.description.length > 6000)
            .map(([offerId]) => offerId);
        const badRich = Object.entries(drafts)
            .filter(([, fields]) => {
                if (typeof fields.rich_content !== 'string' || !fields.rich_content.trim()) return false;
                try { JSON.parse(fields.rich_content); return false; } catch { return true; }
            })
            .map(([offerId]) => offerId);
        if (tooLong.length || badRich.length) {
            setShowConfirm(false);
            showStatus('error', [
                tooLong.length ? `Аннотация длиннее 6000 символов: ${tooLong.slice(0, 5).join(', ')}${tooLong.length > 5 ? '…' : ''}` : '',
                badRich.length ? `Rich-контент — не JSON: ${badRich.slice(0, 5).join(', ')}${badRich.length > 5 ? '…' : ''}` : '',
            ].filter(Boolean).join('. ') + '. Исправьте и отправьте снова.');
            return;
        }
        setShowConfirm(false);
        setSubmitting(true);
        setSubmitResult(null);

        const changes = Object.entries(drafts).map(([offerId, fields]) => ({
            offer_id: offerId,
            ...fields,
        }));

        try {
            const result = await request(`/api/v1/ozon-desc/${accountId}/submit`, {
                method: 'POST',
                body: JSON.stringify({ changes }),
            });
            setSubmitResult(result);
            setDrafts({});
            setSeoTransfers({});
            showStatus('success', `Отправлено: ${result.items_sent} товаров. Task IDs: ${(result.task_ids || []).join(', ')}`);
        } catch (err) {
            showStatus('error', `Ошибка отправки: ${err.message}`);
        } finally {
            setSubmitting(false);
        }
    }, [accountId, drafts, request, showStatus]);

    // ------------------------------------------------------------------ //
    //  Render                                                             //
    // ------------------------------------------------------------------ //
    if (view === 'history') {
        return (
            <div className="desc-panel">
                <div className="desc-toolbar">
                    <button className="desc-btn" onClick={() => setView('editor')}>
                        ← Назад к редактору
                    </button>
                </div>
                <OzonDescriptionHistory accountId={accountId} />
            </div>
        );
    }

    return (
        <div className="desc-panel">
            {/* Toolbar */}
            <div className="desc-toolbar">
                <button
                    className="desc-btn desc-btn--primary"
                    onClick={() => {
                        if (changedCount > 0 && !window.confirm(
                            `Загрузить актуальные данные из Ozon? Неотправленные правки (${changedCount}) будут потеряны.`
                        )) return;
                        loadProducts(true);
                    }}
                    disabled={loading}
                >
                    {loading ? 'Загрузка...' : 'Загрузить актуальные'}
                </button>

                <input
                    className="desc-search"
                    type="text"
                    placeholder="Поиск по артикулу, SKU, названию..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />

                <button
                    type="button"
                    className={`desc-change-filter${onlyChanged ? ' active' : ''}`}
                    onClick={() => { setOnlyChanged((value) => !value); setPage(1); }}
                    aria-pressed={onlyChanged}
                    disabled={changedCount === 0}
                >
                    <span className="desc-change-filter__check">{onlyChanged ? '✓' : ''}</span>
                    Только товары с изменениями
                </button>

                <span className="desc-counter">
                    Товаров: {filtered.length}
                    {selectedOfferIds.size > 0 && (
                        <span className="desc-counter--selected"> | Выбрано: {selectedOfferIds.size}</span>
                    )}
                    {changedCount > 0 && (
                        <span className="desc-counter--changed"> | Изменено: {changedCount}</span>
                    )}
                </span>

                <button
                    className="desc-btn desc-btn--rich"
                    onClick={massRichFromDescription}
                    disabled={products.length === 0}
                    title="Перенести аннотацию в пустые Rich-контенты для всех товаров"
                >
                    Перенести описание в Rich
                </button>

                <button
                    className="desc-btn desc-btn--delim"
                    onClick={() => setShowDelimModal(true)}
                    disabled={products.length === 0}
                    title={selectedOfferIds.size > 0 ? `Заменить разделитель в выделенных (${selectedOfferIds.size})` : 'Заменить разделитель во всех товарах'}
                >
                    Сменить разделитель{selectedOfferIds.size > 0 ? ` (${selectedOfferIds.size})` : ''}
                </button>

                {delimSnapshot && (
                    <button
                        className="desc-btn desc-btn--revert"
                        onClick={undoDelimiterChange}
                        title="Откатить последнее изменение разделителя"
                    >
                        ↩ Откатить разделитель
                    </button>
                )}

                <button
                    className="desc-btn desc-btn--accent primary"
                    disabled={changedCount === 0 || submitting}
                    onClick={() => setShowConfirm(true)}
                >
                    {submitting ? 'Отправка...' : `Отправить изменения (${changedCount})`}
                </button>

                <button className="desc-btn" onClick={() => setView('history')}>
                    История
                </button>
            </div>

            {/* Submit result */}
            {submitResult && (
                <div className="desc-result">
                    Батч: {submitResult.batch_id?.slice(0, 8)}...
                    &nbsp;| Отправлено: {submitResult.items_sent}
                    &nbsp;| Пропущено: {submitResult.items_skipped}
                    {submitResult.task_ids?.length > 0 && (
                        <>&nbsp;| Task IDs: {submitResult.task_ids.join(', ')}</>
                    )}
                    <button className="desc-result__close" onClick={() => setSubmitResult(null)}>×</button>
                </div>
            )}

            {/* Table */}
            <div className="desc-scroll">
                <table className="desc-table">
                    <thead>
                        <tr>
                            <th className="desc-th desc-th--check">
                                <input
                                    type="checkbox"
                                    checked={selectedOfferIds.size === filtered.length && filtered.length > 0}
                                    onChange={(e) => e.target.checked ? selectAll() : selectNone()}
                                    title="Выбрать все"
                                />
                            </th>
                            <th className="desc-th desc-th--num">№</th>
                            <th className="desc-th desc-th--offer">Артикул</th>
                            <th className="desc-th desc-th--sku">SKU</th>
                            <th className="desc-th desc-th--name">Название</th>
                            <th className="desc-th desc-th--desc">Аннотация</th>
                            <th className="desc-th desc-th--desclen">Длина</th>
                            <th className="desc-th desc-th--rich">Rich-контент (11254)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageData.map((product, idx) => {
                            const globalIdx = (safePage - 1) * PAGE_SIZE + idx;
                            const draft = drafts[product.offer_id] || {};
                            const hasNameChange = 'name' in draft;
                            const hasDescChange = 'description' in draft;
                            const hasRichChange = 'rich_content' in draft;

                            const displayName = hasNameChange ? draft.name : (product.name || '');
                            const displayDesc = (hasDescChange ? draft.description : product.description) ?? '';
                            const displayRich = hasRichChange ? draft.rich_content : (product.rich_content || '');

                            const descForRich = (draft.description) ?? (product.description || '');
                            const canMakeRich = descForRich.trim().length > 0;

                            const descLength = displayDesc.length;
                            const isDescTooLong = descLength > 6000;

                            const isSelected = selectedOfferIds.has(product.offer_id);
                            // Перенос из «Точечных изменений» может менять только название —
                            // стрелки описания у него не показываем.
                            const hasSeoTransfer = typeof seoTransfers[product.offer_id]?.seoText === 'string';
                            const showingSeoText = isSeoText(product.offer_id);

                            return (
                                <tr key={product.offer_id} className={`desc-tr ${isSelected ? 'desc-tr--selected' : ''} ${isDescTooLong ? 'desc-tr--toolong' : ''}`}>
                                    <td className="desc-td desc-td--check">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleSelect(product.offer_id)}
                                        />
                                    </td>
                                    <td className="desc-td desc-td--num">{globalIdx + 1}</td>
                                    <td className="desc-td desc-td--offer">
                                        <span className="desc-cell-text">{product.offer_id}</span>
                                    </td>
                                    <td className="desc-td desc-td--sku">
                                        <span className="desc-cell-text">{product.sku}</span>
                                    </td>
                                    <td className={`desc-td desc-td--name${hasNameChange ? ' desc-td--changed' : ''}`}>
                                        <textarea
                                            className="desc-input"
                                            value={displayName}
                                            onChange={(e) => updateDraft(product.offer_id, 'name', e.target.value)}
                                            onPaste={(e) => handlePaste(e, globalIdx, 'name')}
                                            rows={2}
                                        />
                                    </td>
                                    <td className={`desc-td desc-td--desc${hasDescChange ? ' desc-td--changed' : ''}${hasSeoTransfer && showingSeoText ? ' desc-td--seo' : ''}`}>
                                        <div className="desc-desc-wrap">
                                            <textarea
                                                className={`desc-input desc-input--tall ${hasSeoTransfer && showingSeoText ? 'desc-input--seo' : ''}`}
                                                value={displayDesc}
                                                onChange={(e) => updateDraft(product.offer_id, 'description', e.target.value)}
                                                onPaste={(e) => handlePaste(e, globalIdx, 'description')}
                                                rows={4}
                                                title={hasSeoTransfer && showingSeoText ? 'Скопирован из SEO уникализации' : ''}
                                            />
                                            {hasSeoTransfer && (
                                                <div className="desc-seo-arrows">
                                                    <button
                                                        className="desc-seo-arrow desc-seo-arrow--left"
                                                        onClick={() => revertSeoTransfer(product.offer_id)}
                                                        title="Вернуть оригинальное описание"
                                                    >
                                                        ←
                                                    </button>
                                                    <button
                                                        className="desc-seo-arrow desc-seo-arrow--right"
                                                        onClick={() => restoreSeoTransfer(product.offer_id)}
                                                        title="Восстановить SEO описание"
                                                        disabled={showingSeoText}
                                                    >
                                                        →
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td
                                        className={`desc-td desc-td--desclen${isDescTooLong ? ' desc-td--desclen--over' : ''}`}
                                        title={isDescTooLong ? `Длина ${descLength} символов, допустимо не больше 6000` : `${descLength} символов`}
                                    >
                                        <span className={`desc-len-badge${isDescTooLong ? ' desc-len-badge--over' : ''}`}>
                                            {descLength}
                                        </span>
                                    </td>
                                    <td className={`desc-td desc-td--rich${hasRichChange ? ' desc-td--changed' : ''}`}>
                                        <div className="desc-rich-wrap">
                                            <textarea
                                                className="desc-input desc-input--mono"
                                                value={displayRich}
                                                onChange={(e) => updateDraft(product.offer_id, 'rich_content', e.target.value)}
                                                rows={4}
                                            />
                                            {canMakeRich && (
                                                <button
                                                    className="desc-btn desc-btn--micro"
                                                    onClick={() => richFromDescription(product.offer_id)}
                                                    title="Создать Rich из аннотации"
                                                >
                                                    Рич из описания
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {pageData.length === 0 && (
                            <tr>
                                <td className="desc-td desc-td--empty" colSpan={8}>
                                    {loading ? 'Загрузка...' : products.length === 0 ? 'Нажмите "Загрузить актуальные"' : 'Ничего не найдено'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="desc-pagination">
                    <button className="desc-btn" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>
                        ←
                    </button>
                    <span className="desc-pagination__info">
                        {safePage} / {totalPages}
                    </span>
                    <button className="desc-btn" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>
                        →
                    </button>
                </div>
            )}

            {/* Reformat modal */}
            {showDelimModal && (
                <OzonDelimModal
                    products={products}
                    drafts={drafts}
                    selectedOfferIds={selectedOfferIds}
                    onApply={handleDelimApply}
                    onClose={() => setShowDelimModal(false)}
                />
            )}

            {/* Confirm modal */}
            {showConfirm && (
                <div className="desc-modal-overlay" onClick={() => setShowConfirm(false)}>
                    <div className="desc-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Подтверждение отправки</h3>
                        <p>Будет отправлено <strong>{changedCount}</strong> товаров в OZON.</p>
                        <p className="desc-modal__note">
                            Перед отправкой создаётся резервный снимок текущих значений.
                            Изменения применяются через OZON /v3/product/import.
                        </p>
                        <div className="desc-modal__list">
                            {Object.entries(drafts).slice(0, 20).map(([offerId, fields]) => (
                                <div key={offerId} className="desc-modal__item">
                                    <span className="desc-modal__offer">{offerId}</span>
                                    <span className="desc-modal__fields">
                                        {Object.keys(fields).map((f) => (
                                            <span key={f} className="desc-modal__field">{f === 'name' ? 'Название' : f === 'description' ? 'Аннотация' : 'Rich'}</span>
                                        ))}
                                    </span>
                                </div>
                            ))}
                            {changedCount > 20 && (
                                <div className="desc-modal__item desc-modal__item--more">
                                    ...и ещё {changedCount - 20}
                                </div>
                            )}
                        </div>
                        <div className="desc-modal__actions">
                            <button className="desc-btn" onClick={() => setShowConfirm(false)}>Отмена</button>
                            <button className="desc-btn desc-btn--danger" onClick={handleSubmit}>
                                Отправить в OZON
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default OzonDescriptionPanel;
